import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useInputGroup } from '../../Hooks/useInputGroup';
import { dateToStr, tryStrToDate } from '../../Utils/stringUtils';
import {
  DatePickerTextEdit,
  IDatePickerTextEditProps,
} from './Components/DatePickerTextEdit';
import { IEditComponentProps } from '../../Components/Attributes/EditableAttribute/EditComponentTypes';
import {
  DatePicker as MuiDatePicker,
  PickersDay,
  PickersDayProps,
} from '@mui/x-date-pickers-pro';
import { isRedDay } from '../../Utils/dayNames';
import EDBox from '../EDBox/EDBox';

interface IProps extends IEditComponentProps<any> {
  onDateChange?: (newValue: string, isValidDate: boolean) => void;
  eventOnChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  /**OnBlur med samma parametrar som MuiDatePicker */
  onDateBlur?: (
    event: React.FocusEvent<HTMLDivElement | HTMLInputElement>,
    newValue: string,
    isValidDate: boolean
  ) => void;
  noParsing?: boolean;
  parseParams?: string;
  disableFutureDates?: boolean;
  disablePastDates?: boolean;
  className?: string;
  allowOnlyNumbers?: boolean;
}

const Holidays = (props: PickersDayProps & { highlightedDays?: number[] }) => {
  const { day, ...other } = props;
  const redDay = isRedDay(day);

  return (
    <EDBox
      data-testid={'Holidays'}
      component={'span'}
      sx={
        redDay
          ? theme => ({
              '& > :first-of-type': {
                color: theme.palette.error.main,
              },
            })
          : undefined
      }
    >
      <PickersDay {...other} day={day} />
    </EDBox>
  );
};
/**
 * En komponent för att välja datum, med möjlighet att öppna datumväljare i form av en drop-down.
 */
export const EDDatePicker: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const {
    value,
    onBlur,
    onDateBlur,
    noChangeOnBlur,
    disableFutureDates,
    disablePastDates,
    onChange,
    onDateChange,
    allowOnlyNumbers,
    ...textProps
  } = props;
  const [calendarOpen, setCalendarOpen] = useState(false);
  const handleChange = useCallback(
    (newValue: string, isValidDate: boolean) => {
      if (allowOnlyNumbers) {
        newValue = newValue.replace(/[^0-9\-,./]/g, '');
      }
      onChange?.(newValue);
      onDateChange?.(newValue, isValidDate);
    },
    [onChange, onDateChange, allowOnlyNumbers]
  );

  const {
    onFocus,
    ref: fullscreenInputRef,
    active: inputGroupActive,
    navigateNext: inputGroupGoNext,
  } = useInputGroup(props.onFocus, props.disabled);

  const textFieldProps: IDatePickerTextEditProps = {
    editValue: value,
    editOnChange: handleChange,
    noChangeOnBlur: noChangeOnBlur,
    editOnBlur: onDateBlur ?? onBlur,
    editOnFocus: onFocus,
    textEditProps: textProps,
    buttonOnClick: () => {
      setCalendarOpen(!calendarOpen);
    },
    inputGroupActive: inputGroupActive,
    inputGroupGoNext: inputGroupGoNext,
    inputGroupRef: fullscreenInputRef,
  };

  const datePicker = (
    <MuiDatePicker
      slots={{
        textField: DatePickerTextEdit,
        day: Holidays,
      }}
      enableAccessibleFieldDOMStructure={false}
      slotProps={{
        toolbar: { hidden: true },
        textField: textFieldProps,
      }}
      value={tryStrToDate(value ?? '') ?? null}
      open={calendarOpen}
      displayWeekNumber={true}
      onChange={date => {
        if (date != null && typeof date === 'object') {
          onChange?.(dateToStr(date));
          onDateChange?.(dateToStr(date), true);
        } else {
          onChange?.('');
          onDateChange?.('', false);
        }
      }}
      onClose={() => {
        setCalendarOpen(false);
      }}
      disableFuture={disableFutureDates}
      disablePast={disablePastDates}
    />
  );

  return (
    <>
      {(!inputGroupActive || props.fullscreenStillRenderTextEdit) && datePicker}
      {fullscreenInputRef &&
        inputGroupActive &&
        createPortal(datePicker, fullscreenInputRef)}
    </>
  );
};

export default EDDatePicker;
